import React, { useRef, useEffect, useState } from 'react';
import Icon from 'components/AppIcon';

export default function GameCanvas({ gameData, isPaused, onScoreUpdate, onLivesUpdate, onGameOver, onWin }) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const gameStateRef = useRef({
    player: { x: 200, y: 300, vx: 0, vy: 0, width: 32, height: 32, onGround: false, facing: 1 },
    platforms: [
      { x: 0, y: 380, width: 800, height: 20 },
      { x: 100, y: 300, width: 120, height: 16 },
      { x: 300, y: 240, width: 120, height: 16 },
      { x: 500, y: 180, width: 120, height: 16 },
      { x: 650, y: 280, width: 100, height: 16 },
      { x: 200, y: 160, width: 80, height: 16 },
    ],
    coins: [
      { x: 140, y: 270, collected: false },
      { x: 340, y: 210, collected: false },
      { x: 540, y: 150, collected: false },
      { x: 680, y: 250, collected: false },
      { x: 420, y: 340, collected: false },
      { x: 230, y: 130, collected: false },
    ],
    enemies: [
      { x: 320, y: 348, vx: 1.2, width: 28, height: 28, alive: true, startX: 280, range: 120 },
      { x: 550, y: 348, vx: -1, width: 28, height: 28, alive: true, startX: 480, range: 140 },
    ],
    particles: [],
    score: 0,
    lives: 3,
    keys: {},
    gameOver: false,
    won: false,
    frame: 0,
    invincible: 0,
  });

  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 400 });
  const containerRef = useRef(null);
  const livesRef = useRef(3);
  const wonRef = useRef(false);
  const gameOverRef = useRef(false);

  useEffect(() => {
    const resize = () => {
      if (containerRef?.current) {
        const w = containerRef?.current?.clientWidth;
        const h = Math.min(Math.max(w * 0.5, 280), 480);
        setCanvasSize({ width: w, height: h });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      gameStateRef.current.keys[e.code] = e?.type === 'keydown';
    };
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('keyup', handleKey);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const gs = gameStateRef?.current;
    const scaleX = canvasSize?.width / 800;
    const scaleY = canvasSize?.height / 400;

    const spawnParticle = (x, y, color, count = 6) => {
      for (let i = 0; i < count; i++) {
        gs?.particles?.push({
          x: x * scaleX, y: y * scaleY,
          vx: (Math.random() - 0.5) * 4,
          vy: (Math.random() - 0.5) * 4 - 2,
          life: 1, color, size: Math.random() * 4 + 2,
        });
      }
    };

    const loop = () => {
      if (!isPaused && !gs?.gameOver && !gs?.won) {
        gs.frame++;
        if (gs?.invincible > 0) gs.invincible--;
        const { player, platforms, coins, enemies, keys } = gs;

        // Input
        const speed = 4.5;
        if (keys?.['ArrowLeft'] || keys?.['KeyA']) { player.vx = -speed; player.facing = -1; }
        else if (keys?.['ArrowRight'] || keys?.['KeyD']) { player.vx = speed; player.facing = 1; }
        else player.vx *= 0.75;

        if ((keys?.['ArrowUp'] || keys?.['KeyW'] || keys?.['Space']) && player?.onGround) {
          player.vy = -11;
          player.onGround = false;
        }

        player.vy += 0.5;
        player.x += player?.vx;
        player.y += player?.vy;
        player.onGround = false;

        // Platform collision
        for (const p of platforms) {
          const px = p?.x * scaleX, py = p?.y * scaleY, pw = p?.width * scaleX, ph = p?.height * scaleY;
          const plx = player?.x * scaleX, ply = player?.y * scaleY, plw = player?.width * scaleX, plh = player?.height * scaleY;
          if (plx + plw > px && plx < px + pw && ply + plh > py && ply + plh < py + ph + 14 && player?.vy >= 0) {
            player.y = (py - plh) / scaleY;
            player.vy = 0;
            player.onGround = true;
          }
        }

        // Bounds
        if (player?.x < 0) player.x = 0;
        if (player?.x > 800 - player?.width) player.x = 800 - player?.width;

        // Fall off screen
        if (player?.y > 430) {
          if (gs?.invincible <= 0) {
            gs.lives--;
            spawnParticle(player?.x + 16, 400, '#ef4444', 8);
            if (gs?.lives !== livesRef?.current) {
              livesRef.current = gs?.lives;
              onLivesUpdate && onLivesUpdate(gs?.lives);
            }
            player.x = 200; player.y = 300; player.vx = 0; player.vy = 0;
            gs.invincible = 90;
            if (gs?.lives <= 0) {
              gs.gameOver = true;
              gameOverRef.current = true;
              onGameOver && onGameOver(gs?.score);
            }
          }
        }

        // Coins
        for (const c of coins) {
          if (!c?.collected) {
            const dx = (player?.x + 16) * scaleX - (c?.x + 8) * scaleX;
            const dy = (player?.y + 16) * scaleY - (c?.y + 8) * scaleY;
            if (Math.sqrt(dx * dx + dy * dy) < 26 * scaleX) {
              c.collected = true;
              gs.score += 100;
              spawnParticle(c?.x + 8, c?.y + 8, '#f59e0b', 8);
              onScoreUpdate && onScoreUpdate(gs?.score);
            }
          }
        }

        // Win check
        if (coins?.every(c => c?.collected) && !gs?.won) {
          gs.won = true;
          wonRef.current = true;
          gs.score += 500; // Bonus
          onScoreUpdate && onScoreUpdate(gs?.score);
          onWin && onWin(gs?.score);
        }

        // Enemies
        for (const e of enemies) {
          if (!e?.alive) continue;
          e.x += e?.vx;
          // Patrol within range
          if (e?.x < e?.startX || e?.x > e?.startX + e?.range) e.vx *= -1;

          if (gs?.invincible <= 0) {
            const dx = Math.abs((player?.x + 16) - (e?.x + 14)) * scaleX;
            const dy = Math.abs((player?.y + 16) - (e?.y + 14)) * scaleY;
            if (dx < 22 * scaleX && dy < 22 * scaleY) {
              if (player?.vy > 0 && player?.y + player?.height < e?.y + 12) {
                e.alive = false;
                gs.score += 200;
                spawnParticle(e?.x + 14, e?.y + 14, '#ef4444', 10);
                player.vy = -8;
                onScoreUpdate && onScoreUpdate(gs?.score);
              } else {
                gs.lives--;
                spawnParticle(player?.x + 16, player?.y + 16, '#ef4444', 8);
                if (gs?.lives !== livesRef?.current) {
                  livesRef.current = gs?.lives;
                  onLivesUpdate && onLivesUpdate(gs?.lives);
                }
                player.x = 200; player.y = 300; player.vx = 0; player.vy = 0;
                gs.invincible = 90;
                if (gs?.lives <= 0) {
                  gs.gameOver = true;
                  gameOverRef.current = true;
                  onGameOver && onGameOver(gs?.score);
                }
              }
            }
          }
        }

        // Particles
        gs.particles = gs?.particles?.filter(p => p?.life > 0);
        for (const p of gs?.particles) {
          p.x += p?.vx; p.y += p?.vy; p.vy += 0.15; p.life -= 0.04;
        }
      }

      // ─── DRAW ───
      ctx?.clearRect(0, 0, canvasSize?.width, canvasSize?.height);
      const scX = canvasSize?.width / 800;
      const scY = canvasSize?.height / 400;

      // Sky gradient
      const skyGrad = ctx?.createLinearGradient(0, 0, 0, canvasSize?.height);
      skyGrad?.addColorStop(0, '#0f0f2e');
      skyGrad?.addColorStop(1, '#1e1040');
      ctx.fillStyle = skyGrad;
      ctx?.fillRect(0, 0, canvasSize?.width, canvasSize?.height);

      // Stars
      const stars = [[50,30],[150,60],[300,20],[450,45],[600,15],[720,55],[80,90],[380,80],[550,70],[200,40],[700,30],[480,25]];
      for (const [sx, sy] of stars) {
        const twinkle = 0.4 + 0.3 * Math.sin(gs?.frame * 0.05 + sx);
        ctx.fillStyle = `rgba(255,255,255,${twinkle})`;
        ctx?.beginPath();
        ctx?.arc(sx * scX, sy * scY, 1.5, 0, Math.PI * 2);
        ctx?.fill();
      }

      // Platforms
      for (const p of gs?.platforms) {
        const grad = ctx?.createLinearGradient(0, p?.y * scY, 0, (p?.y + p?.height) * scY);
        grad?.addColorStop(0, '#6366f1');
        grad?.addColorStop(1, '#4338ca');
        ctx.fillStyle = grad;
        ctx?.beginPath();
        ctx?.roundRect(p?.x * scX, p?.y * scY, p?.width * scX, p?.height * scY, 4);
        ctx?.fill();
        // Highlight
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx?.fillRect(p?.x * scX + 4, p?.y * scY, p?.width * scX - 8, 3);
      }

      // Coins
      for (const c of gs?.coins) {
        if (!c?.collected) {
          const bounce = Math.sin(gs?.frame * 0.08 + c?.x * 0.1) * 3;
          ctx?.save();
          ctx.shadowColor = '#f59e0b';
          ctx.shadowBlur = 12;
          ctx.fillStyle = '#f59e0b';
          ctx?.beginPath();
          ctx?.arc((c?.x + 8) * scX, (c?.y + 8 + bounce) * scY, 8 * scX, 0, Math.PI * 2);
          ctx?.fill();
          ctx.fillStyle = '#fde68a';
          ctx?.beginPath();
          ctx?.arc((c?.x + 6) * scX, (c?.y + 5 + bounce) * scY, 3 * scX, 0, Math.PI * 2);
          ctx?.fill();
          ctx?.restore();
        } else {
          // Collected indicator
          ctx.fillStyle = 'rgba(16,185,129,0.3)';
          ctx?.beginPath();
          ctx?.arc((c?.x + 8) * scX, (c?.y + 8) * scY, 6 * scX, 0, Math.PI * 2);
          ctx?.fill();
        }
      }

      // Enemies
      for (const e of gs?.enemies) {
        if (!e?.alive) continue;
        const wobble = Math.sin(gs?.frame * 0.15) * 2;
        ctx?.save();
        ctx.shadowColor = '#ef4444';
        ctx.shadowBlur = 10;
        ctx.fillStyle = '#ef4444';
        ctx?.beginPath();
        ctx?.roundRect(e?.x * scX, (e?.y + wobble) * scY, e?.width * scX, e?.height * scY, 6);
        ctx?.fill();
        // Eyes
        ctx.fillStyle = 'white';
        ctx?.beginPath();
        ctx?.arc((e?.x + 8) * scX, (e?.y + 10 + wobble) * scY, 4 * scX, 0, Math.PI * 2);
        ctx?.arc((e?.x + 20) * scX, (e?.y + 10 + wobble) * scY, 4 * scX, 0, Math.PI * 2);
        ctx?.fill();
        ctx.fillStyle = '#1f2937';
        ctx?.beginPath();
        ctx?.arc((e?.x + 9) * scX, (e?.y + 10 + wobble) * scY, 2 * scX, 0, Math.PI * 2);
        ctx?.arc((e?.x + 21) * scX, (e?.y + 10 + wobble) * scY, 2 * scX, 0, Math.PI * 2);
        ctx?.fill();
        ctx?.restore();
      }

      // Player
      const { player } = gs;
      const isInvincible = gs?.invincible > 0;
      if (!isInvincible || gs?.frame % 6 < 3) {
        ctx?.save();
        ctx.shadowColor = '#818cf8';
        ctx.shadowBlur = 14;
        const pGrad = ctx?.createLinearGradient(player?.x * scX, player?.y * scY, player?.x * scX, (player?.y + player?.height) * scY);
        pGrad?.addColorStop(0, '#a5b4fc');
        pGrad?.addColorStop(1, '#6366f1');
        ctx.fillStyle = pGrad;
        ctx?.beginPath();
        ctx?.roundRect(player?.x * scX, player?.y * scY, player?.width * scX, player?.height * scY, 8);
        ctx?.fill();
        // Eyes
        const eyeOffsetX = player?.facing > 0 ? 0 : 2;
        ctx.fillStyle = 'white';
        ctx?.beginPath();
        ctx?.arc((player?.x + 10 + eyeOffsetX) * scX, (player?.y + 12) * scY, 4 * scX, 0, Math.PI * 2);
        ctx?.arc((player?.x + 22 + eyeOffsetX) * scX, (player?.y + 12) * scY, 4 * scX, 0, Math.PI * 2);
        ctx?.fill();
        ctx.fillStyle = '#1f2937';
        ctx?.beginPath();
        ctx?.arc((player?.x + 11 + eyeOffsetX) * scX, (player?.y + 12) * scY, 2 * scX, 0, Math.PI * 2);
        ctx?.arc((player?.x + 23 + eyeOffsetX) * scX, (player?.y + 12) * scY, 2 * scX, 0, Math.PI * 2);
        ctx?.fill();
        ctx?.restore();
      }

      // Particles
      for (const p of gs?.particles) {
        ctx?.save();
        ctx.globalAlpha = p?.life;
        ctx.fillStyle = p?.color;
        ctx?.beginPath();
        ctx?.arc(p?.x, p?.y, p?.size, 0, Math.PI * 2);
        ctx?.fill();
        ctx?.restore();
      }

      // Coin counter HUD
      const collected = gs?.coins?.filter(c => c?.collected)?.length;
      const total = gs?.coins?.length;
      ctx?.save();
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx?.beginPath();
      ctx?.roundRect(canvasSize?.width - 90 * scX, 10 * scY, 80 * scX, 28 * scY, 6);
      ctx?.fill();
      ctx.fillStyle = '#f59e0b';
      ctx.font = `bold ${Math.round(14 * scX)}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx?.fillText(`🪙 ${collected}/${total}`, canvasSize?.width - 50 * scX, 29 * scY);
      ctx?.restore();

      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animFrameRef?.current);
  }, [isPaused, canvasSize, onScoreUpdate, onLivesUpdate, onGameOver, onWin]);

  const handleTouchMove = (dir) => {
    const gs = gameStateRef?.current;
    gs.keys['ArrowLeft'] = dir === 'left';
    gs.keys['ArrowRight'] = dir === 'right';
  };
  const handleTouchJump = () => {
    gameStateRef.current.keys['Space'] = true;
    setTimeout(() => { gameStateRef.current.keys['Space'] = false; }, 120);
  };
  const clearTouch = () => {
    gameStateRef.current.keys['ArrowLeft'] = false;
    gameStateRef.current.keys['ArrowRight'] = false;
  };

  return (
    <div ref={containerRef} className="w-full relative">
      <canvas
        ref={canvasRef}
        width={canvasSize?.width}
        height={canvasSize?.height}
        className="w-full rounded-xl block"
        style={{ background: '#0f0f2e', touchAction: 'none' }}
        aria-label="Game canvas - use arrow keys or WASD to move, Space to jump"
      />
      {/* Mobile touch controls */}
      <div className="flex md:hidden items-center justify-between mt-3 px-2 gap-3">
        <div className="flex gap-2">
          <button
            onTouchStart={() => handleTouchMove('left')}
            onTouchEnd={clearTouch}
            onMouseDown={() => handleTouchMove('left')}
            onMouseUp={clearTouch}
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl select-none active:scale-95 transition-transform"
            style={{ background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.5)' }}
            aria-label="Move left"
          >
            <Icon name="ChevronLeft" size={24} color="white" />
          </button>
          <button
            onTouchStart={() => handleTouchMove('right')}
            onTouchEnd={clearTouch}
            onMouseDown={() => handleTouchMove('right')}
            onMouseUp={clearTouch}
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl select-none active:scale-95 transition-transform"
            style={{ background: 'rgba(99,102,241,0.3)', border: '1px solid rgba(99,102,241,0.5)' }}
            aria-label="Move right"
          >
            <Icon name="ChevronRight" size={24} color="white" />
          </button>
        </div>
        <button
          onTouchStart={handleTouchJump}
          onMouseDown={handleTouchJump}
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold select-none active:scale-95 transition-transform"
          style={{ background: 'rgba(99,102,241,0.5)', border: '2px solid rgba(129,140,248,0.8)' }}
          aria-label="Jump"
        >
          <Icon name="ArrowUp" size={26} color="white" />
        </button>
      </div>
    </div>
  );
}